import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { useId } from 'react';

/**
 * Shared interface primitives.
 *
 * Deliberately small. Anything genuinely domain-shaped — an event card, a gate
 * decision panel — belongs to the surface that owns that domain, not here. A
 * design system that starts absorbing domain concepts becomes the coupling the
 * module boundaries were drawn to prevent.
 */

export type Tone = 'positive' | 'caution' | 'critical' | 'info' | 'neutral';

/**
 * The glyph is not decoration.
 *
 * A gate decision, a connectivity state and a settlement hold must all be
 * readable without colour — for a colour-blind operator, on a washed-out screen
 * in daylight, or through a phone camera someone is holding up at a gate. Colour
 * is the third signal here, after shape and word.
 */
const TONE_GLYPH: Record<Tone, string> = {
  positive: '✓',
  caution: '!',
  critical: '✕',
  info: 'i',
  neutral: '•',
};

export interface StatusProps {
  readonly tone: Tone;
  readonly label: string;
  /** Overrides the tone's default glyph where the domain has a better one. */
  readonly glyph?: string;
  readonly title?: string;
}

export function Status({ tone, label, glyph, title }: StatusProps) {
  return (
    <span className={`gc-status gc-status--${tone}`} title={title}>
      <span className="gc-status__glyph" aria-hidden="true">
        {glyph ?? TONE_GLYPH[tone]}
      </span>
      {label}
    </span>
  );
}

export interface NoticeProps {
  readonly tone: Tone;
  readonly title?: string;
  readonly children: ReactNode;
  /**
   * Set for anything a user is waiting on — a refusal, a completed action — so
   * a screen reader announces it without the user having to go looking.
   */
  readonly live?: boolean;
}

export function Notice({ tone, title, children, live }: NoticeProps) {
  return (
    <div
      className={`gc-notice gc-notice--${tone}`}
      role={tone === 'critical' ? 'alert' : 'status'}
      aria-live={live === false ? 'off' : tone === 'critical' ? 'assertive' : 'polite'}
    >
      <span className="gc-status__glyph" aria-hidden="true">
        {TONE_GLYPH[tone]}
      </span>
      <div className="gc-notice__body">
        {title !== undefined && <strong className="gc-notice__title">{title}</strong>}
        <div>{children}</div>
      </div>
    </div>
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: 'primary' | 'secondary' | 'quiet' | 'destructive';
  readonly size?: 'default' | 'large';
  readonly block?: boolean;
  /** Renders a busy state and blocks a second submission. */
  readonly busy?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'default',
  block,
  busy,
  children,
  className,
  disabled,
  ...rest
}: ButtonProps) {
  const classes = [
    'gc-button',
    variant === 'secondary' ? '' : `gc-button--${variant}`,
    size === 'large' ? 'gc-button--large' : '',
    block === true ? 'gc-button--block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      {...rest}
      className={classes}
      disabled={disabled === true || busy === true}
      aria-busy={busy === true}
    >
      {busy === true ? 'Working…' : children}
    </button>
  );
}

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly label: string;
  readonly hint?: string;
  readonly error?: string | undefined;
}

export function Field({ label, hint, error, id, ...rest }: FieldProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;

  const describedBy = [hint === undefined ? null : hintId, error === undefined ? null : errorId]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div className="gc-field">
      <label className="gc-field__label" htmlFor={fieldId}>
        {label}
      </label>
      <input
        {...rest}
        id={fieldId}
        className="gc-input"
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy.length === 0 ? undefined : describedBy}
      />
      {hint !== undefined && (
        <span className="gc-field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error !== undefined && (
        <span className="gc-field__error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
}

export interface CardProps {
  readonly title?: string;
  readonly eyebrow?: string;
  readonly action?: ReactNode;
  readonly children: ReactNode;
  readonly flush?: boolean;
}

export function Card({ title, eyebrow, action, children, flush }: CardProps) {
  return (
    <section className={flush === true ? 'gc-card gc-card--flush' : 'gc-card'}>
      {(title !== undefined || action !== undefined) && (
        <header
          className="gc-card__header"
          style={flush === true ? { padding: '1.25rem' } : undefined}
        >
          <div className="gc-stack gc-stack--tight">
            {eyebrow !== undefined && <span className="gc-eyebrow">{eyebrow}</span>}
            {title !== undefined && <h2>{title}</h2>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export interface EmptyStateProps {
  readonly title: string;
  readonly children: ReactNode;
  readonly action?: ReactNode;
}

/**
 * An empty state that says *why* it is empty.
 *
 * "No events yet" and "events do not exist in this release" are different facts,
 * and a product that renders them identically has told the user something
 * untrue (kickoff Section 17).
 */
export function EmptyState({ title, children, action }: EmptyStateProps) {
  return (
    <div className="gc-empty">
      <h3 className="gc-empty__title">{title}</h3>
      <div className="gc-empty__body">{children}</div>
      {action}
    </div>
  );
}

export function Skeleton({ lines = 3 }: { readonly lines?: number }) {
  return (
    <div className="gc-stack gc-stack--tight" aria-hidden="true">
      {Array.from({ length: lines }, (_, index) => (
        <div
          key={index}
          className="gc-skeleton"
          style={{ width: `${String(100 - index * 12)}%` }}
        />
      ))}
    </div>
  );
}

export function LoadingRegion({ label }: { readonly label: string }) {
  return (
    <div role="status" aria-live="polite">
      <span className="gc-visually-hidden">{label}</span>
      <Skeleton />
    </div>
  );
}

export interface ChecklistItem {
  readonly code: string;
  readonly message: string;
  readonly action?: ReactNode;
}

/**
 * What still stands between something and being done.
 *
 * Used for publish readiness. Two decisions matter: it shows what is *done* as
 * well as what is not, so an organizer can see progress rather than only
 * obstacles; and each outstanding item carries a way to go and fix it, because
 * "blocked" without a destination is how a support ticket starts.
 */
export function Checklist({
  outstanding,
  doneLabel,
  readyTitle,
  readyBody,
}: {
  readonly outstanding: readonly ChecklistItem[];
  readonly doneLabel?: string;
  readonly readyTitle: string;
  readonly readyBody: ReactNode;
}) {
  if (outstanding.length === 0) {
    return (
      <Notice tone="positive" title={readyTitle}>
        {readyBody}
      </Notice>
    );
  }

  return (
    <div className="gc-stack gc-stack--tight">
      {doneLabel !== undefined && <span className="gc-eyebrow">{doneLabel}</span>}
      <ul className="gc-checklist">
        {outstanding.map((item) => (
          <li key={item.code} className="gc-checklist__item">
            <span className="gc-checklist__marker" aria-hidden="true">
              ○
            </span>
            <span className="gc-checklist__body">
              <span>{item.message}</span>
              {item.action}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A labelled figure. Used across the organizer workspace for capacity. */
export function Metric({
  label,
  value,
  hint,
  tone,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
  readonly tone?: Tone;
}) {
  return (
    <div className="gc-metric">
      <span className="gc-eyebrow">{label}</span>
      <span
        className="gc-metric__value"
        style={tone === undefined ? undefined : { color: `var(--gc-signal-${tone})` }}
      >
        {value}
      </span>
      {hint !== undefined && <span className="gc-muted">{hint}</span>}
    </div>
  );
}
